import {
  Avatar,
  Box,
  Button,
  Container,
  DialogActions,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { isKycVerified, renderIfElse } from "../../../../utils";
import { useTranslation } from "react-i18next";
import * as propTypes from "prop-types";
import CryptoIcon from "../../../../assets/logo/crypto-white.svg";
import WireIcon from "../../../../assets/logo/wire-white.svg";
import WireIconBlue from "../../../../assets/logo/wire-blue.svg";
import CryptoIconBlue from "../../../../assets/logo/crypto-themed.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBankForm,
  getCurrenciesData,
  getPockytConnectedBanks,
  getSerivceProvider,
} from "../../../../store/actions";
import AppScreenLoader from "../../../../components/AppScreenLoader";
import { cryptoflow, fiatVendors, paymentOptions } from "../../../../utils/constants";
import AppDialogHalfScreen from "../../../../components/AppDialogHalfScreen";
import { getFiatCounterLimit, logEvent, userCreate } from "../../../../store/api";
import { EVENTS } from "../../../../utils/events";
import CurrencyList from "./CurrencyListStep2";
import AppLoader from "../../../../components/AppLoader";
import { getUserSelector } from "../../../../utils/selectorHelpers";
export default function Step2({ payment, styles, handlePaymentMethod, amount }) {
  const [openDisclaimer, setOpenDisclaimer] = React.useState(false);
  const dispatch = useDispatch();
  const fiatService = useSelector(({ dashboard }) => dashboard?.fiatService);
  const currencies = useSelector(({ currencies }) => currencies.currencies);
  const [loadingBankLimit, setLoadingBankLimit] = useState(false);
  const account = useSelector(({ Auth }) => Auth.account);
  const { t } = useTranslation();
  const user = useSelector(getUserSelector);
  useEffect(() => {
    dispatch(clearBankForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getFiatLimitReached = async (amount) => {
    try {
      setLoadingBankLimit(true);
      const currency = account?.payoutAccount?.configuration?.currency;
      const data = await getFiatCounterLimit(amount, currency);
      const isLimitReached = !!data?.result?.error;
      if (isLimitReached) {
        logEvent({
          current: EVENTS.BANK_TRANSFER.BANK_POPUP_LIMIT_REACHED,
          activity: EVENTS.BANK_TRANSFER.BANK_POPUP_LIMIT_REACHED,
          meta: {},
        });
      }
      setLoadingBankLimit(false);
      return isLimitReached;
    } catch (err) {
      setLoadingBankLimit(false);
      return false;
    }
  };
  useEffect(() => {
    dispatch(getCurrenciesData());
    dispatch(getSerivceProvider());
    dispatch(getPockytConnectedBanks());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!currencies) {
    return <AppScreenLoader />;
  }
  const handlePaymentMethodAction = async (ev) => {
    if (!fiatService?.enableFiat) {
      return;
    }
    const isLimitReached = await getFiatLimitReached(amount);
    logEvent({
      current: EVENTS.BANK_TRANSFER.BANK_STEP_SELECTED,
      activity: EVENTS.BANK_TRANSFER.BANK_STEP_SELECTED,
      meta: {},
    });
    if (isLimitReached) {
      handleDisclaimerOpen();
      return;
    }
    if (isKycVerified(user)) {
      userCreate();
    }
    handlePaymentMethod(ev, paymentOptions[fiatService?.serviceName]);
  };
  const handleDisclaimerOpen = () => {
    setOpenDisclaimer(true);
  };
  const handleDisclaimerClose = () => {
    setOpenDisclaimer(false);
    logEvent({
      current: EVENTS.BANK_TRANSFER.BANK_POPUP_LIMIT_REACHED_OK,
      activity: EVENTS.BANK_TRANSFER.BANK_POPUP_LIMIT_REACHED_OK,
      meta: {},
    });
  };
  function BankTransferDisclaimerModal() {
    return (
      <AppDialogHalfScreen
        show={openDisclaimer}
        onChangeShow={() => handleDisclaimerClose()}
        title={t("bank.limitReached.title")}
        centralize={true}
      >
        <Box>
          <Typography
            color="#D4D4D8"
            fontSize={14}
            fontWeight={400}
            marginTop={2}
            fontFamily={"Manrope"}
          >
            {t("bank.limitReached.description")}
          </Typography>

        </Box>
        <DialogActions
          sx={{
            marginTop: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            autoFocus
            onClick={() => handleDisclaimerClose()}
            variant="contained"
            color="primary"
            sx={{
              color: "#F8FAFF",
              textTransform: "capitalize",
              borderRadius: "8px",
              ":disabled": { backgroundColor: "#0088F5", opacity: 0.4 },
            }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </AppDialogHalfScreen>
    );
  }

  return (
    <Paper
      sx={{
        backgroundColor: "transparent",
        boxShadow: "none",
        paddingTop: 1,
        width: "100%",
      }}
    >
      {!currencies?.length && !fiatService?.enableFiat && (
        <Container maxWidth="md" disableGutters>
          <Typography
            variant="body2"
            fontSize={16}
            marginTop={5}
            textAlign={"left"}
          >
            {t("NoPaymentMethod")}{" "}
            <Link
              href="mailto:support@rocketfuel.inc"
              target="_blank"
              color={"#0088F5"}
            >
              support@rocketfuel.inc
            </Link>
          </Typography>
        </Container>
      )}
      <List>
        {!!currencies?.length && fiatService?.cryptoProvider === cryptoflow.FLOW1 && (
          <ListItem
            aria-label="tracker"
            aria-describedby="transfer_method_crypto"
            style={payment === paymentOptions.crypto ? styles.selected : {}}
            onClick={(ev) => handlePaymentMethod(ev, paymentOptions.crypto)}
          >
            <ListItemAvatar>
              <Avatar
                aria-label="tracker"
                aria-describedby="transfer_method_crypto"
              >
                {renderIfElse(
                  payment === "crypto",
                  <CryptoIconBlue
                    aria-label="tracker"
                    aria-describedby="transfer_method_crypto"
                  />,
                  <CryptoIcon
                    aria-label="tracker"
                    aria-describedby="transfer_method_crypto"
                  />
                )}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={t("crypto.transfer")}
              secondary={t("crypto.transfer.duration")}
            />
          </ListItem>
        )}
        {!!currencies?.length && (fiatService?.cryptoProvider === cryptoflow.FLOW2
        || fiatService?.cryptoProvider === cryptoflow.FLOW4
        || fiatService?.cryptoProvider === cryptoflow.FLOW3) && (
          <CurrencyList handlePaymentMethod={handlePaymentMethod} payment={payment} />
        )}
        {!!fiatService?.showSidebar && <ListItem
          aria-label="tracker"
          aria-describedby="transfer_method_fiat"
          style={fiatVendors.includes(payment) ? styles.selected : {}}
          onClick={handlePaymentMethodAction}
          disabled={!fiatService?.enableFiat}
        >
          <ListItemAvatar
            aria-label="tracker"
            aria-describedby="transfer_method_fiat"
          >
            <Avatar
              aria-label="tracker"
              aria-describedby="transfer_method_fiat"
            >
              {renderIfElse(
                payment === "non-crypto",
                <WireIconBlue
                  aria-label="tracker"
                  aria-describedby="transfer_method_fiat"
                />,
                <WireIcon
                  aria-label="tracker"
                  aria-describedby="transfer_method_fiat"
                />
              )}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={t("bank.transfer")}
          // secondary={t('bank.transfer.duration')}
          />
          <ListItemSecondaryAction> {loadingBankLimit && <AppLoader />}</ListItemSecondaryAction>
        </ListItem>}
      </List>
      {(!fiatService?.enableFiat && !!fiatService?.showSidebar) && <Typography fontSize={14}>{t('bankDisabled')}</Typography>}
      <BankTransferDisclaimerModal />
    </Paper>
  );
}

Step2.propTypes = {
  payment: propTypes.any,
  paymentOptions: propTypes.any,
  styles: propTypes.any,
  handlePaymentMethod: propTypes.any,
};
